import { get, post, put } from '@/services/http';
import { RuleList } from '@/collections/rules';

/**
 * Tracking data extracted from UserPackageModel for analytics
 *
 * Properties that may not be present in the base model but are needed
 * for tracking purposes (e.g., code, isYearly, finalPrice)
 */
export interface PackageTrackingData {
  packageCode: string;
  packageName: string;
  price: number;
  discountCode?: string;
  isYearly: boolean;
  finalPrice: number;
  billingPackageId: number;
  discountApplied: boolean;
}

export class UserPackageModel {
  public id: number;
  public name?: string;
  public price: number;
  public discount: number;
  public durationLimitInIntervals?: number;
  public activatedAt?: string;
  public rules: RuleList;
  public status?: string;
  public chargeId?: number;
  public billingPackageId: number;
  public discountCode?: string;
  public discountId?: number;
  public billingPackageVersion?: number;
  public cancelledAt?: string;
  public trialEndAt?: string;
  public createdAt?: string;
  public updatedAt?: string;
  public interval: string = 'EVERY_30_DAYS';
  public predictEtlTime?: string;
  public predictEtlMinutes: number = -1;
  public etlPlanOrders: Array<{ startTime: string; endTime: string; apiCount: number }> = [];
  public etlPlanProducts: Array<{ startTime: string; endTime: string; apiCount: number }> = [];

  constructor(data: any = {}) {
    this.id = data.id;
    this.billingPackageId = data.billingPackageId;
    this.rules = new RuleList();
    this.discount = data.discount || 0;
    this.price = data.price || 0;
  }

  public get passedTrial() {
    return (this.toTimestamp(undefined) - this.toTimestamp(this.trialEndAt)) > 0;
  }

  public toTimestamp(strDate: string|undefined) {
    let datum: number = 0;
    if (!strDate) {
      datum = Date.now();
    } else {
      datum = Date.parse(strDate);
    }
    return datum / 1000;
  }

  public async fetch() {
    const res: any = await get(`/current_package`);
    this.mapData(res.data);
  }

  public async create(trialDays: number = 7, discount: number = 0,
                      returnUrl: string, needInstallPlugins: number[] = [], isYearly: boolean = false) {

    let interval: string = 'EVERY_30_DAYS';
    if (isYearly) {
      interval = 'ANNUAL';
    }
    const res: any = await post(`/user_packages`, {
      billingPackageId: this.billingPackageId,
      trialDays,
      discountPercentage: discount,
      returnUrl,
      discountCode: this.discountCode,
      plugins: needInstallPlugins,
      interval,
    });
    this.mapData(res.data.userPackage);
    return res.data.confirmationUrl;
  }

  public async confirmed() {
    const res: any = await put(`/user_packages/active`, {
      chargeId: this.chargeId,
    });
    this.mapData(res.data.userPackage);
  }

  public async getCurrent() {
    try {
      const res: any = await get(`/user_packages/current`);
      this.mapData(res.data.userPackage);
      this.etlPlanOrders = res.data.etlPlanOrders || [];
      this.etlPlanProducts = res.data.etlPlanProducts || [];
      if (this.activatedAt && res.data.predictETLTime) {
        const activatedAt = new Date(this.activatedAt);
        // predictETLTime is in seconds
        const predictReadyAt = new Date(
          activatedAt.getTime() + res.data.predictETLTime * 1000,
        );
        const now = new Date();
        const diffMs = predictReadyAt.getTime() - now.getTime();
        const diffMinutes = Math.floor(diffMs / 60000);

        this.predictEtlMinutes = diffMinutes;
        if (diffMinutes <= 0) {
          this.predictEtlTime = 'Data should be ready soon.';
        } else if (diffMinutes < 60) {
          this.predictEtlTime = `Estimated ready in ~${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}.`;
        } else {
          const hours = Math.floor(diffMinutes / 60);
          const mins = diffMinutes % 60;
          if (mins > 0) {
            this.predictEtlTime = `Estimated ready in ~${hours}h ${mins}m.`;
          } else {
            this.predictEtlTime = `Estimated ready in ~${hours} hour${hours > 1 ? 's' : ''}.`;
          }
        }
      }
    } catch (e) {
      // console.log(e);
      // skipped
    }
  }

  public mapData(data: any = {}) {
    this.id = data.id;
    this.name = data.name;
    this.price = data.price;
    this.discount = data.discount;
    this.durationLimitInIntervals = data.durationLimitInIntervals;
    this.activatedAt = data.activatedAt;
    this.rules = new RuleList();
    this.rules.mapData(data.rules);
    this.status = data.status;
    this.chargeId = data.chargeId;
    this.billingPackageId = data.billingPackageId;
    this.billingPackageVersion = data.billingPackageVersion;
    this.discountId = data.discountId;
    this.cancelledAt = data.cancelledAt;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.interval = data.interval;
    this.trialEndAt = data.trialEndAt;
  }

  /**
   * Extract tracking data from this package in a type-safe manner
   *
   * Centralizes the logic for extracting analytics tracking properties,
   * handling defaults for properties that may not be present on the model.
   * This eliminates the need for repeated `as any` casts throughout the codebase.
   *
   * @returns {PackageTrackingData} Type-safe tracking data object
   *
   * @example
   * ```typescript
   * const trackingData = this.currentPackage.getTrackingData();
   * this.pricingTracker.trackSubscriptionConfirmed({
   *   packageCode: trackingData.packageCode,
   *   packageName: trackingData.packageName,
   *   // ... other properties
   * });
   * ```
   */
  public getTrackingData(): PackageTrackingData {
    // Access dynamic properties that may be added by API but not in model definition
    const dynamicData = this as any;

    return {
      packageCode: dynamicData.code || 'unknown',
      packageName: this.name || 'Unknown Package',
      price: this.price || 0,
      discountCode: this.discountCode,
      isYearly: dynamicData.isYearly || false,
      finalPrice: dynamicData.finalPrice || this.price || 0,
      billingPackageId: this.billingPackageId || 0,
      discountApplied: !!this.discountCode,
    };
  }
}
